import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { getMaterialMacrosTemplate } from 'apollo/templates/material-macro';
import { MaterialMacro } from 'models/material-macro';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';

type MaterialMacroMultiSelectProps = {
    selectedMaterialMacroIds: string[];
    onChange: (materialMacroIds: string[]) => void;
};

const MaterialMacroMultiSelect: FC<MaterialMacroMultiSelectProps> = ({
    selectedMaterialMacroIds,
    onChange,
}: MaterialMacroMultiSelectProps) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { materialMacros } = { materialMacros: [] } } = useQuery<{ materialMacros: MaterialMacro[] }>(
        getMaterialMacrosTemplate,
        {
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const materialMacroOptions = materialMacros || [];
    const selectedMaterialMacros = materialMacroOptions.filter((materialMacro) =>
        selectedMaterialMacroIds?.includes(materialMacro._id as string),
    );

    return (
        <Autocomplete
            multiple
            id="material-macro-multiselect"
            options={materialMacroOptions}
            value={selectedMaterialMacros}
            onChange={(_event, selected: MaterialMacro[]) => onChange(selected.map((macro) => macro._id as string))}
            getOptionLabel={(option: MaterialMacro) => option?.name || ''}
            isOptionEqualToValue={(option: MaterialMacro, value: MaterialMacro) => option?._id === value?._id}
            renderTags={(value: MaterialMacro[], getTagProps) =>
                value.map((option: MaterialMacro, index: number) => (
                    <Chip {...getTagProps({ index })} key={option._id} label={option.name} size="small" />
                ))
            }
            renderInput={(params) => (
                <TextField {...params} label={translate('lab-settings:MATERIAL_MACROS')} fullWidth margin="dense" />
            )}
        />
    );
};

export default MaterialMacroMultiSelect;
